import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import styles from "./BlogsPage.module.css";
import { blogsHeroImage } from "./blogsData";
import { blogService } from "../../services/blog.service";
import { getSecondaryKeywords } from "../../utils/blogHelpers";
import { highlightGoldCoast } from "../../utils/highlightGoldCoast";

const BlogsPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");

  useEffect(() => {
    let isMounted = true;

    blogService.getBlogs().then((data) => {
      if (!isMounted) return;
      setBlogs(data);
      setLoading(false);
    });

    return () => {
      isMounted = false;
    };
  }, []);

  const categories = useMemo(() => {
    const unique = Array.from(new Set(blogs.map((blog) => blog.category).filter(Boolean)));
    return ["All", ...unique];
  }, [blogs]);

  const featuredBlog = blogs.find((blog) => blog.featured) || blogs[0];

  const filteredBlogs = useMemo(() => {
    if (activeCategory === "All") return blogs;
    return blogs.filter((blog) => blog.category === activeCategory);
  }, [activeCategory, blogs]);

  return (
    <div className={styles.blogPage}>
      <section
        className={styles.heroSection}
        style={{
          backgroundImage: `linear-gradient(90deg, rgba(0, 18, 38, 0.22) 0%, rgba(0, 33, 61, 0.14) 45%, rgba(0, 0, 0, 0.06) 100%), url(${blogsHeroImage})`,
        }}
      >
        <div className={styles.heroInner}>
          <div className={styles.heroContent}>
            <h1>Travel Tips, Accommodation Guides & Local Insights</h1>

            <p>
              {highlightGoldCoast(
                "Explore helpful articles about romantic Tweed Heads South, NSW stays, Kirra Beach accommodation, last-minute airport stays, and why travelers choose Arabella Motor Inn in Southport. The inn is in Tweed Heads, about a ten-minute drive from Tweed Heads South, NSW Airport, with free Wi-Fi and a non-smoking property."
              )}
            </p>
          </div>
        </div>
      </section>

      <section className={styles.featuredSection}>
        <div className={styles.container}>
          <div className={styles.sectionTitle}>
            <span>Featured Article</span>
            <h2>Latest From Our Blog</h2>
            <p>
              {highlightGoldCoast(
                "Handpicked travel and accommodation guide for your next comfortable Tweed Heads South, NSW stay, whether that is a night between flights, a family visit, or a few days on the coast."
              )}
            </p>
          </div>

          {loading ? (
            <div className={styles.emptyState}>Loading stories...</div>
          ) : featuredBlog ? (
            <div className={styles.featuredCard}>
              <Link
                to={`/blogs/${featuredBlog.slug}`}
                className={styles.featuredImage}
              >
                {featuredBlog.image ? (
                  <img src={featuredBlog.image} alt={featuredBlog.title} />
                ) : null}
                <span className={styles.featuredImageTag}>Featured</span>
              </Link>

              <div className={styles.featuredContent}>
                <div className={styles.blogMeta}>
                  <span className={styles.category}>{featuredBlog.category}</span>
                  <span>{featuredBlog.date}</span>
                  <span>•</span>
                  <span>{featuredBlog.readTime}</span>
                </div>

                <Link
                  to={`/blogs/${featuredBlog.slug}`}
                  className={styles.featuredTitleLink}
                >
                  {featuredBlog.title}
                </Link>

                <p>{featuredBlog.shortDescription}</p>

                <div className={styles.cardPills}>
                  {getSecondaryKeywords(featuredBlog).slice(0, 3).map((keyword) => (
                    <span className={styles.keywordPill} key={keyword}>
                      {keyword}
                    </span>
                  ))}
                  {(featuredBlog.tags || []).slice(0, 4).map((tag) => (
                    <span className={styles.tagPill} key={tag}>
                      {tag}
                    </span>
                  ))}
                </div>

                <Link
                  to={`/blogs/${featuredBlog.slug}`}
                  className={styles.readButton}
                >
                  Read More <span>→</span>
                </Link>
              </div>
            </div>
          ) : (
            <div className={styles.emptyState}>
              New stories will appear here once they are published.
            </div>
          )}
        </div>
      </section>

      <section className={styles.blogListSection} id="blog-list">
        <div className={styles.container}>
          <div className={styles.blogListHeader}>
            <div>
              <span>Explore Articles</span>
              <h2>Helpful Travel Guides</h2>
              <p>
                Choose a topic and read useful information before planning your
                next trip.
              </p>
            </div>

            <div className={styles.categoryTabs}>
              {categories.map((category) => (
                <button
                  key={category}
                  type="button"
                  onClick={() => setActiveCategory(category)}
                  className={activeCategory === category ? styles.activeTab : ""}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.blogGrid}>
            {filteredBlogs.map((blog) => (
              <article className={styles.blogCard} key={blog.id}>
                <Link to={`/blogs/${blog.slug}`} className={styles.blogImage}>
                  {blog.image ? <img src={blog.image} alt={blog.title} /> : null}
                  <span className={styles.blogCategory}>{blog.category}</span>
                </Link>

                <div className={styles.blogContent}>
                  <div className={styles.blogInfo}>
                    <span>{blog.date}</span>
                    <span>•</span>
                    <span>{blog.readTime}</span>
                  </div>

                  <Link
                    to={`/blogs/${blog.slug}`}
                    className={styles.blogTitleLink}
                  >
                    {blog.title}
                  </Link>

                  <p>{blog.shortDescription}</p>

                  <div className={styles.cardPills}>
                    {getSecondaryKeywords(blog).slice(0, 2).map((keyword) => (
                      <span className={styles.keywordPill} key={keyword}>
                        {keyword}
                      </span>
                    ))}
                    {(blog.tags || []).slice(0, 3).map((tag) => (
                      <span className={styles.tagPill} key={tag}>
                        {tag}
                      </span>
                    ))}
                  </div>

                  <Link
                    to={`/blogs/${blog.slug}`}
                    className={styles.cardReadBtn}
                  >
                    Read More <span>→</span>
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.ctaSection}>
        <div className={styles.container}>
          <div className={styles.ctaBox}>
            <div className={styles.ctaImageBox}>
              <img
                src="/images/blogs/travel-transits.png"
                alt="Travel and accommodation tips"
              />
            </div>

            <div className={styles.ctaContent}>
              <span>Stay Updated</span>
              <h2>Get Travel & Accommodation Tips</h2>
              <p>
                Stay connected with Arabella Motor Inn for helpful travel guides,
                local destination tips, and accommodation updates.
              </p>

              <div className={styles.subscribeBox}>
                <input type="email" placeholder="Enter your email" />
                <button type="button">Subscribe</button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogsPage;
